// SPDX-License-Identifier: AGPL-3.0-or-later

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import Channels from '@app/features/channel/state/Channels';
import * as ChannelUtils from '@app/features/channel/utils/ChannelUtils';
import {stopPropagationOnEnterSpace} from '@app/features/input/utils/KeyboardUtils';
import styles from '@app/features/invite/components/InviteListItem.module.css';
import {useInviteCountdown} from '@app/features/invite/hooks/useInviteCountdown';
import {isGuildInvite} from '@app/features/invite/types/InviteTypes';
import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import Users from '@app/features/user/state/Users';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import * as DateUtils from '@app/features/user/utils/dateFormatting';
import {ChannelTypes} from '@app/vendor/constants/ChannelConstants';
import {formatShortRelativeTime} from '@app/vendor/date_utils/DateDuration';
import type {Invite} from '@app/vendor/schema/domains/invite/InviteSchemas';
import {Trans, useLingui} from '@lingui/react/macro';
import {ClipboardIcon, XIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useMemo} from 'react';
export const InviteListHeader = observer(
	({showChannel = false, showCreatedDate = false}: {showChannel?: boolean; showCreatedDate?: boolean}) => {
		return (
			<div className={showChannel ? styles.header : styles.headerWithoutChannel}>
				<div className={styles.headerColumn}>
					<Trans>Inviter</Trans>
				</div>
				{showChannel && (
					<div className={styles.headerColumn}>
						<Trans>Channel</Trans>
					</div>
				)}
				<div className={styles.headerColumn}>
					<Trans>Code</Trans>
				</div>
				<div className={styles.headerColumn}>
					<Trans>Uses</Trans>
				</div>
				<div className={styles.headerColumn}>{showCreatedDate ? <Trans>Created</Trans> : <Trans>Expires</Trans>}</div>
			</div>
		);
	},
);
export const InviteListItem: React.FC<{
	invite: Invite;
	onRevoke: (code: string) => void;
	showChannel?: boolean;
	showCreatedDate?: boolean;
	onMobilePress?: (invite: Invite) => void;
}> = observer(({invite, onRevoke, showChannel = false, showCreatedDate = false, onMobilePress}) => {
	const {t, i18n} = useLingui();
	const {countdown, isMonospace} = useInviteCountdown(invite.expires_at);
	const inviter = Users.getUser(invite.inviter?.id || '');
	const avatarUrl = inviter ? AvatarUtils.getUserAvatarURL(inviter, false) : null;
	const {enabled: isMobile} = MobileLayout;
	const guildInvite = isGuildInvite(invite) ? invite : null;
	const channelFromState = guildInvite ? Channels.getChannel(guildInvite.channel.id) : null;
	const categoryFromState = channelFromState ? Channels.getChannel(channelFromState.parentId || '') : null;
	const channel = showChannel ? channelFromState : null;
	const category = showChannel && channelFromState?.parentId ? categoryFromState : null;
	const usesText = useMemo(() => {
		if (!guildInvite) {
			return '0';
		}
		const currentUses = guildInvite.uses ?? 0;
		const maxUses = guildInvite.max_uses ?? 0;
		if (maxUses > 0) {
			return `${currentUses} / ${maxUses}`;
		}
		return String(currentUses);
	}, [guildInvite]);
	const dateDisplay = useMemo(() => {
		if (showCreatedDate) {
			if (!guildInvite?.created_at) {
				return '';
			}
			const createdDate = new Date(guildInvite.created_at);
			return formatShortRelativeTime(createdDate) || '';
		}
		return countdown || t`Never`;
	}, [showCreatedDate, guildInvite, countdown]);
	const dateTooltip = useMemo(() => {
		if (showCreatedDate) {
			if (!guildInvite?.created_at) {
				return null;
			}
			const createdDate = new Date(guildInvite.created_at);
			return DateUtils.getFormattedDateTimeWithSeconds(createdDate);
		}
		return null;
	}, [showCreatedDate, guildInvite]);
	const dateIsMonospace = !showCreatedDate && isMonospace;
	const handleCopy = (e: React.MouseEvent) => {
		e.stopPropagation();
		TextCopyCommands.copy(i18n, `${RuntimeConfig.inviteEndpoint}/${invite.code}`);
	};
	const handleRowClick = () => {
		if (isMobile) {
			if (onMobilePress) {
				onMobilePress(invite);
				return;
			}
			TextCopyCommands.copy(i18n, `${RuntimeConfig.inviteEndpoint}/${invite.code}`);
		}
	};
	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (isMobile && (e.key === 'Enter' || e.key === ' ')) {
			e.preventDefault();
			handleRowClick();
		}
	};
	const itemClass = isMobile ? styles.mobileItem : showChannel ? styles.itemWithChannel : styles.itemWithoutChannel;
	const itemContent = (
		<>
			<div className={styles.inviter}>
				<span className={styles.label}>
					<Trans>Inviter:</Trans>
				</span>
				{inviter && avatarUrl ? (
					<>
						<img src={avatarUrl} alt="" className={styles.avatar} loading="lazy" />
						<span className={styles.username}>{inviter.username}</span>
					</>
				) : (
					<span className={styles.usernameUnknown}>Unknown</span>
				)}
			</div>
			{showChannel && channel && (
				<div className={styles.channel}>
					<span className={styles.label}>
						<Trans>Channel:</Trans>
					</span>
					{ChannelUtils.getIcon(channel, {size: 20, className: styles.channelIcon})}
					<div className={styles.channelInfo}>
						<span className={styles.channelName}>{channel.name}</span>
						{channel.type !== ChannelTypes.GUILD_CATEGORY && (
							<span className={styles.categoryName}>{category ? category.name : t`No Category`}</span>
						)}
					</div>
				</div>
			)}
			<div className={styles.code}>
				<span className={styles.label}>
					<Trans>Code:</Trans>
				</span>
				<code className={styles.inviteCode}>{invite.code}</code>
				<Tooltip text={t`Click to copy`}>
					<FocusRing offset={-2}>
						<button
							type="button"
							onClick={handleCopy}
							className={styles.copyButtonHidden}
							aria-label={t`Copy invite link`}
							onKeyDown={stopPropagationOnEnterSpace}
						>
							<ClipboardIcon className={styles.copyIcon} />
						</button>
					</FocusRing>
				</Tooltip>
			</div>
			<div className={styles.uses}>
				<span className={styles.label}>
					<Trans>Uses:</Trans>
				</span>
				<span className={styles.usesText}>{usesText}</span>
			</div>
			<div className={styles.date}>
				<span className={styles.label}>{showCreatedDate ? <Trans>Created:</Trans> : <Trans>Expires:</Trans>}</span>
				{dateTooltip ? (
					<Tooltip text={dateTooltip}>
						<span className={dateIsMonospace ? styles.dateTextMonospace : styles.dateText}>{dateDisplay}</span>
					</Tooltip>
				) : (
					<span className={dateIsMonospace ? styles.dateTextMonospace : styles.dateText}>{dateDisplay}</span>
				)}
			</div>
			<Tooltip text={t`Revoke`}>
				<FocusRing offset={-2}>
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							onRevoke(invite.code);
						}}
						className={styles.revokeButtonHidden}
						aria-label={t`Revoke Invite`}
						onKeyDown={stopPropagationOnEnterSpace}
					>
						<XIcon className={styles.revokeIcon} weight="bold" />
					</button>
				</FocusRing>
			</Tooltip>
		</>
	);
	if (isMobile) {
		return (
			<div role="button" onClick={handleRowClick} onKeyDown={handleKeyDown} tabIndex={0} className={itemClass}>
				{itemContent}
			</div>
		);
	}
	return <div className={itemClass}>{itemContent}</div>;
});
